// Audit logs — Supabase implementation. Owner-only read, append-only.
//
// Writes come from write_audit() inside the SECURITY DEFINER business
// functions. There is no insert path from the browser and no UPDATE/DELETE
// policy at all, so the trail cannot be edited after the fact.

import { supabase } from '@/lib/supabase';
import { tsReq } from './mappers';
import type { Database } from '@/types/database';

type Row = Database['public']['Tables']['audit_logs']['Row'];

export interface AuditLogEntry {
  id: string;
  action: string;
  entity: string;
  entityId: string;
  eventId: string | null;
  actorId: string | null;
  actorEmail: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  createdAt: number;

  // Aliases kept so the AuditLogs screen renders unchanged. The Firestore
  // writers disagreed on the actor field — Cloud Functions wrote `createdBy`,
  // the SPA wrote `userId`/`userEmail` — and the viewer read all of them.
  // Postgres normalizes to actor_id/actor_email; these mirror it.
  userId: string;
  createdBy: string;
  userEmail: string | null;
  userDisplayName: string | null;
  /** Lifted out of `after` for display; most writers put it there. */
  reason: string | null;
}

function mapEntry(r: Row): AuditLogEntry {
  const after = (r.after ?? null) as Record<string, unknown> | null;
  return {
    id: r.id,
    action: r.action,
    entity: r.entity,
    entityId: r.entity_id ?? '',
    eventId: r.event_id,
    actorId: r.actor_id,
    actorEmail: r.actor_email,
    before: (r.before ?? null) as Record<string, unknown> | null,
    after,
    createdAt: tsReq(r.created_at),
    userId: r.actor_id ?? '',
    createdBy: r.actor_id ?? '',
    userEmail: r.actor_email,
    userDisplayName: r.actor_email,
    reason: (after?.reason as string | undefined) ?? null,
  };
}

export interface ListAuditLogsOptions {
  /** Server-side filter, backed by the (entity, created_at) index. */
  entity?: string;
  eventId?: string;
  /**
   * Client-side prefix match. Postgres has no index-friendly prefix operator
   * for this without a trigram index, and it only sees the newest `limit`
   * rows — a match older than that window will not be found.
   */
  actionPrefix?: string;
  limit?: number;
}

export async function listAuditLogs(
  opts: ListAuditLogsOptions = {},
): Promise<AuditLogEntry[]> {
  const limit = opts.limit ?? 200;
  let q = supabase
    .from('audit_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (opts.entity) q = q.eq('entity', opts.entity);
  if (opts.eventId) q = q.eq('event_id', opts.eventId);

  const { data, error } = await q;
  if (error) throw new Error(error.message);

  let rows = (data ?? []).map(mapEntry);
  if (opts.actionPrefix) {
    rows = rows.filter((r) => r.action.startsWith(opts.actionPrefix!));
  }
  return rows;
}
